import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@/hooks/useToast';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import type { Organization } from '@/types/events';
import {
  buildNewShifts,
  compareShifts,
  diffShifts,
  hasShiftChanges,
  shiftToDraft,
  shiftsOutsideEventDates,
  type NewShiftInput,
  type ShiftDraft,
} from '@/utils/eventShifts';
import { fetchEventShifts, saveEventShifts } from '@/utils/eventShiftsApi';

// Import utilities
import {
  validateForm,
  validateImage,
  validateDepartment,
  setEndDateToEndOfDay,
  uploadImageToStorage,
  deleteImageFromStorage,
  checkActiveEventsLimit,
  createEventChat,
  type EventFormData,
} from '@/pages/Organizations/components/shared/utils';

type FormMode = 'create' | 'edit';

/**
 * One shared empty list, so clearing shifts that are already empty is a no-op for React
 * instead of a new array identity on every render.
 */
const NO_SHIFTS: ShiftDraft[] = [];

interface BaseEventFormProps {
  mode: FormMode;
  userId: string;
  event?: Organization | null;
  subscriptionActive?: boolean;
  onSuccess: () => void;
  onSubscriptionPrompt?: () => void;
  onEndEventConfirm?: () => Promise<void>;
}

export function useEventFormBase({
  mode,
  userId,
  event,
  subscriptionActive = true,
  onSuccess,
  onSubscriptionPrompt,
  onEndEventConfirm,
}: BaseEventFormProps) {
  const isEdit = mode === 'edit';
  const initialEvent = isEdit && event ? event : null;

  // State
  const [name, setName] = useState(initialEvent?.name || '');
  const [location, setLocation] = useState(initialEvent?.location || '');
  const [description, setDescription] = useState(initialEvent?.description || '');
  const [startDate, setStartDate] = useState<string>(initialEvent?.start_date || '');
  const [endDate, setEndDate] = useState<string>(initialEvent?.end_date || '');
  const [slots, setSlots] = useState(initialEvent ? String(initialEvent.slots || '') : '');
  const [status, setStatus] = useState<'Scheduled' | 'Active'>(
    (initialEvent?.status as 'Scheduled' | 'Active') || 'Scheduled'
  );
  const [isPublic, setIsPublic] = useState(initialEvent?.is_public ?? true);
  const [eventImage, setEventImage] = useState<File | null>(null);
  const [eventImagePreview, setEventImagePreview] = useState<string | null>(initialEvent?.image || null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [departments, setDepartments] = useState<string[]>(initialEvent?.departments || []);
  const [newDepartment, setNewDepartment] = useState('');
  // Shifts live in their own table, so edit mode loads them separately from the event row.
  const [shifts, setShifts] = useState<ShiftDraft[]>(NO_SHIFTS);
  const [originalShifts, setOriginalShifts] = useState<ShiftDraft[]>(NO_SHIFTS);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { showToast, hideToast, toast } = useToast();
  const isSavingRef = useRef(false);
  const originalEventRef = useRef<Organization | null>(initialEvent);
  // resetForm is called from an effect keyed on its own identity (EventModal), so it must
  // not change when the shifts it restores change -- that effect would run again, reset the
  // shifts to a fresh array, and loop ("Maximum update depth exceeded").
  const originalShiftsRef = useRef<ShiftDraft[]>(originalShifts);
  originalShiftsRef.current = originalShifts;
  // The event whose shifts the form should hold. A slower response for an event the modal has
  // since moved away from must not land in the form: its shifts would be shown -- and could be
  // deleted -- as if they belonged to the event now open.
  const shiftsEventIdRef = useRef<string | null>(isEdit && event?.id ? event.id : null);
  shiftsEventIdRef.current = isEdit && event?.id ? event.id : null;

  // Initialize form when event changes (edit mode only)
  useEffect(() => {
    if (isEdit && event) {
      originalEventRef.current = event;
      setName(event.name || '');
      setLocation(event.location || '');
      setDescription(event.description || '');
      setStartDate(event.start_date || '');
      setEndDate(event.end_date || '');
      setSlots(String(event.slots || ''));
      setStatus((event.status as 'Scheduled' | 'Active') || 'Scheduled');
      setIsPublic(event.is_public ?? true);
      setEventImage(null);
      setEventImagePreview(event.image || null);
      setDepartments(event.departments || []);
      setNewDepartment('');
      setRemoveExistingImage(false);
      setHasChanges(false);
    }
  }, [isEdit, event]);

  const loadShifts = useCallback(async (eventId: string) => {
    setShiftsLoading(true);
    const loaded = await fetchEventShifts(eventId);
    if (shiftsEventIdRef.current !== eventId) return true;
    const drafts = loaded?.length ? loaded.map(shiftToDraft) : NO_SHIFTS;
    setOriginalShifts(drafts);
    setShifts(drafts);
    setShiftsLoading(false);
    return loaded !== null;
  }, []);

  useEffect(() => {
    if (!isEdit || !event?.id) {
      setShifts(NO_SHIFTS);
      setOriginalShifts(NO_SHIFTS);
      return;
    }
    void loadShifts(event.id).then((ok) => {
      if (!ok) showToast('Could not load this event\'s shifts. Close and reopen to try again.', 'error');
    });
    // showToast is recreated every render; reloading on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, event?.id, loadShifts]);

  const shiftsChanged = useMemo(
    () => hasShiftChanges(diffShifts(originalShifts, shifts)),
    [originalShifts, shifts],
  );

  // Check for changes (edit mode only)
  useEffect(() => {
    if (!isEdit || !originalEventRef.current) return;

    const original = originalEventRef.current;

    const normalizeDate = (date?: string | null) => {
      if (!date) return null;
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized.getTime();
    };

    const datesChanged =
      normalizeDate(original.start_date) !== normalizeDate(startDate) ||
      normalizeDate(original.end_date) !== normalizeDate(endDate);

    const changed =
      original.name !== name ||
      original.location !== location ||
      (original.description || '') !== description ||
      original.status !== status ||
      original.is_public !== isPublic ||
      eventImage !== null ||
      removeExistingImage ||
      JSON.stringify(original.departments || []) !== JSON.stringify(departments) ||
      shiftsChanged ||
      Number(original.slots || 0) !== Number(slots || 0) ||
      datesChanged;

    setHasChanges(changed);
  }, [
    isEdit,
    name,
    location,
    description,
    startDate,
    endDate,
    slots,
    status,
    isPublic,
    eventImage,
    removeExistingImage,
    departments,
    shiftsChanged,
  ]);

  // Image handlers
  const handleImageChange = (file: File | null) => {
    if (file) {
      const error = validateImage(file);
      if (error) {
        showToast(error, 'error');
        return;
      }
    }
    setRemoveExistingImage(false);
    setEventImage(file);

    if (isEdit) {
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setEventImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setEventImagePreview(event?.image || null);
      }
    }
  };

  const handleRemoveExistingImage = () => {
    // Clear any newly selected image
    setEventImage(null);

    if (isEdit) {
      // In edit mode, mark that we want to delete the current stored image
      setRemoveExistingImage(true);
      setEventImagePreview(null);
    } else {
      // In create mode, just clear preview
      setEventImagePreview(null);
    }
  };

  // Department handlers
  const handleAddDepartment = () => {
    const error = validateDepartment(newDepartment, departments);
    if (error) {
      showToast(error, 'error');
      return;
    }
    if (newDepartment.trim()) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
    }
  };

  const handleRemoveDepartment = (dept: string) => {
    setDepartments(departments.filter((d) => d !== dept));
  };

  // Shift handlers
  const handleAddShifts = (input: NewShiftInput): boolean => {
    const result = buildNewShifts(shifts, input);
    if (result.error !== null) {
      showToast(result.error, 'error');
      return false;
    }
    setShifts([...shifts, ...result.drafts].sort(compareShifts));
    return true;
  };

  const handleRemoveShift = (key: string) => {
    setShifts(shifts.filter((s) => s.key !== key));
  };

  const handleShiftCapacityChange = (key: string, capacity: number | null) => {
    setShifts(shifts.map((s) => (s.key === key ? { ...s, capacity } : s)));
  };

  // Reset form
  const resetForm = useCallback(() => {
    if (isEdit && originalEventRef.current) {
      // Reset to original values
      const original = originalEventRef.current;
      setName(original.name || '');
      setLocation(original.location || '');
      setDescription(original.description || '');
      setStartDate(original.start_date || '');
      setEndDate(original.end_date || '');
      setSlots(String(original.slots || ''));
      setStatus((original.status as 'Scheduled' | 'Active') || 'Scheduled');
      setIsPublic(original.is_public ?? true);
      setEventImage(null);
      setEventImagePreview(original.image || null);
      setRemoveExistingImage(false);
      setDepartments(original.departments || []);
      setShifts(originalShiftsRef.current);
      setNewDepartment('');
      setHasChanges(false);
    } else {
      // Reset to empty
      setName('');
      setLocation('');
      setDescription('');
      setStartDate('');
      setEndDate('');
      setSlots('');
      setStatus('Scheduled');
      setIsPublic(true);
      setEventImage(null);
      setEventImagePreview(null);
      setDepartments([]);
      setShifts(NO_SHIFTS);
      setOriginalShifts(NO_SHIFTS);
      setNewDepartment('');
      setRemoveExistingImage(false);
      setHasChanges(false);
    }
  }, [isEdit]);

  // Save/Create handler
  const handleSave = async () => {
    if (subscriptionActive === false) {
      showToast(
        `Subscription Required: You need an active subscription to ${isEdit ? 'edit' : 'create'} events.`,
        'error'
      );
      if (onSubscriptionPrompt) {
        setTimeout(() => {
          onSubscriptionPrompt();
        }, 500);
      }
      return;
    }

    // Prevent multiple submissions
    if (isSaving || isSavingRef.current) {
      return;
    }

    const formData: EventFormData = {
      name,
      location,
      description,
      slots,
      status,
      startDate,
      endDate,
      departments,
    };

    const validationError = validateForm(formData);
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }

    const slotsValue = parseInt(slots, 10);
    if (!slots || isNaN(slotsValue) || slotsValue <= 0) {
      showToast('Number of slots must be greater than 0', 'error');
      return;
    }

    if (shiftsOutsideEventDates(shifts, startDate, endDate).length > 0) {
      showToast('Some shifts fall outside the event dates. Remove them or change the dates.', 'error');
      return;
    }

    setIsSaving(true);
    isSavingRef.current = true;

    try {
      if (isEdit) {
        // EDIT MODE
        await handleEdit(slotsValue);
        showToast('Event updated successfully!', 'success');
      } else {
        // CREATE MODE
        const shiftWarning = await handleCreate(slotsValue);
        if (shiftWarning) {
          showToast(shiftWarning, 'error');
        } else {
          showToast('Event created successfully!', 'success');
        }
      }

      onSuccess();
    } catch (error) {
      logger.error(`Error ${isEdit ? 'updating' : 'creating'} event:`, error);
      showToast(error instanceof Error ? error.message : `Failed to ${isEdit ? 'update' : 'create'} event`, 'error');
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };

  // Create logic. Resolves to a warning when the event was created but its shifts were not.
  const handleCreate = async (slotsValue: number): Promise<string | null> => {
    const { allowed } = await checkActiveEventsLimit(userId);
    if (!allowed) {
      throw new Error(
        'Event Limit Reached: You can only have a maximum of 3 active events at the same time. Please end one of your current events before creating a new one.'
      );
    }

    const now = new Date();
    const eventData = {
      name: name.trim(),
      location: location.trim(),
      description: description.trim().length > 0 ? description.trim().slice(0, 500) : undefined,
      slots: slotsValue,
      status: status,
      is_public: isPublic,
      start_date: status === 'Scheduled' && startDate ? new Date(startDate).toISOString() : null,
      end_date: status === 'Scheduled' && endDate ? setEndDateToEndOfDay(new Date(endDate)).toISOString() : null,
      departments: departments.length > 0 ? departments : [],
      created_by: userId,
      created_at: now.toISOString(),
      image: null,
      is_active: true,
    };

    const { data: newEventData, error: eventError } = await supabase
      .from('events')
      .insert([eventData])
      .select()
      .single();

    if (eventError) {
      throw new Error(`Failed to create event: ${eventError.message}`);
    }

    let imageUrl: string | null = null;
    if (eventImage && userId) {
      const fileExt = eventImage.name.split('.').pop();
      const fileName = `event_${newEventData.id}-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;
      imageUrl = await uploadImageToStorage(eventImage, 'event-images', filePath);

      if (imageUrl) {
        const { error: updateError } = await supabase
          .from('events')
          .update({ image: imageUrl })
          .eq('id', newEventData.id);

        if (updateError) {
          logger.warn('Failed to update event with image:', updateError);
        }
      }
    }

    await createEventChat(newEventData.id, newEventData.name, userId, imageUrl);

    // The event already exists at this point, so a failure here must not throw: the form
    // would stay filled in and saving again would create a second event.
    let shiftWarning: string | null = null;
    try {
      await saveEventShifts(newEventData.id, [], shifts);
    } catch (error) {
      logger.error('Event created but its shifts failed to save:', error);
      shiftWarning = 'Event created, but its shifts could not be saved. Edit the event to add them again.';
    }

    resetForm();
    return shiftWarning;
  };

  // Edit logic
  const handleEdit = async (slotsValue: number) => {
    if (!event) throw new Error('No event to edit');

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('Authentication error. Please log in again.');
    }

    let imageUrl = event.image;

    // Dacă utilizatorul a șters explicit poza existentă
    if (removeExistingImage && !eventImage && event.image) {
      await deleteImageFromStorage(event.image);
      imageUrl = null;
    }

    if (eventImage) {
      const fileExt = eventImage.name.split('.').pop();
      const fileName = `event_${event.id}-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;
      imageUrl = await uploadImageToStorage(eventImage, 'event-images', filePath);

      if (!imageUrl) {
        throw new Error('Failed to upload image. Please try again.');
      }

      if (event.image && event.image.trim() !== '' && event.image !== imageUrl) {
        await deleteImageFromStorage(event.image);
      }
    }

    if (status === 'Scheduled' && startDate && endDate) {
      const startTime = new Date(startDate).getTime();
      const endTime = setEndDateToEndOfDay(new Date(endDate)).getTime();

      if (endTime <= startTime) {
        throw new Error('End date must be after start date.');
      }
    }

    const { error: updateError } = await supabase
      .from('events')
      .update({
        name: name.trim(),
        location: location.trim(),
        description: description.trim().length > 0 ? description.trim().slice(0, 500) : undefined,
        slots: slotsValue,
        status: status,
        is_public: isPublic,
        start_date: status === 'Scheduled' && startDate ? new Date(startDate).toISOString() : null,
        end_date: status === 'Scheduled' && endDate ? setEndDateToEndOfDay(new Date(endDate)).toISOString() : null,
        departments: departments.length > 0 ? departments : [],
        image: imageUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', event.id)
      .eq('created_by', userId);

    if (updateError) {
      throw new Error(`Failed to update event: ${updateError.message}`);
    }

    // Safe to retry on failure: the event update above is idempotent, and re-running the
    // diff skips whatever already went through.
    if (hasShiftChanges(diffShifts(originalShifts, shifts))) {
      await saveEventShifts(event.id, originalShifts, shifts);
      await loadShifts(event.id);
    }

    // Update original ref so hasChanges becomes false; clear pending image state
    originalEventRef.current = {
      ...event,
      name: name.trim(),
      location: location.trim(),
      description: description.trim().length > 0 ? description.trim().slice(0, 500) : undefined,
      slots: slotsValue,
      status,
      is_public: isPublic,
      start_date: status === 'Scheduled' && startDate ? new Date(startDate).toISOString() : null,
      end_date: status === 'Scheduled' && endDate ? setEndDateToEndOfDay(new Date(endDate)).toISOString() : null,
      departments: departments.length > 0 ? departments : [],
      image: imageUrl,
      updated_at: new Date().toISOString(),
    };
    setEventImage(null);
    setRemoveExistingImage(false);
    setEventImagePreview(imageUrl ?? null);
    setHasChanges(false);
  };

  // End event handler (edit mode only)
  const handleEndEvent = () => {
    if (onEndEventConfirm) {
      onEndEventConfirm();
      return;
    }
    showToast('Please confirm ending the event', 'info');
  };

  const executeEndEvent = async () => {
    if (!isEdit || !event) return;
    if (isSaving || isSavingRef.current) return;

    setIsSaving(true);
    isSavingRef.current = true;

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error('Authentication error. Please log in again.');
      }

      const now = new Date();
      const updateData: {
        is_active: boolean;
        updated_at: string;
        start_date?: string;
        end_date?: string;
      } = {
        is_active: false,
        updated_at: now.toISOString(),
      };

      if (!event.end_date) {
        const startDateObj = event.start_date ? new Date(event.start_date) : null;

        if (startDateObj) {
          if (startDateObj > now) {
            updateData.end_date = new Date(startDateObj.getTime() + 1000).toISOString();
          } else {
            updateData.end_date = now.toISOString();
          }
        } else {
          updateData.start_date = now.toISOString();
          updateData.end_date = new Date(now.getTime() + 1000).toISOString();
        }
      }

      const { error } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', event.id)
        .eq('created_by', userId);

      if (error) {
        throw new Error(error.message || 'Failed to end event');
      }

      showToast('Event ended successfully!', 'success');
      onSuccess();
    } catch (error) {
      logger.error('Error ending event:', error);
      showToast(error instanceof Error ? error.message : 'Failed to end event', 'error');
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };

  return {
    // State
    name,
    location,
    description,
    startDate,
    endDate,
    slots,
    status,
    isPublic,
    eventImage,
    eventImagePreview,
    departments,
    shifts,
    shiftsLoading,
    newDepartment,
    isSaving,
    hasChanges,
    toast,
    // Setters
    setName,
    setLocation,
    setDescription,
    setStartDate,
    setEndDate,
    setSlots,
    setStatus,
    setIsPublic,
    setNewDepartment,
    // Handlers
    handleImageChange,
    handleRemoveExistingImage,
    handleAddDepartment,
    handleRemoveDepartment,
    handleAddShifts,
    handleRemoveShift,
    handleShiftCapacityChange,
    handleSave,
    handleEndEvent: isEdit ? handleEndEvent : undefined,
    executeEndEvent: isEdit ? executeEndEvent : undefined,
    resetForm,
    hideToast,
  };
}
